import { type TeamId, type TeamData, isDefined, isNotDefined } from '@terros/common'

export const MAX_TEAMS_PER_LEVEL = 8

export type WithParentId = Pick<TeamData, 'teamId' | 'parentId'>

export type SortableTeam = WithParentId & Pick<TeamData, 'name'>

export type TeamRelations<T extends SortableTeam> = {
  teamsById: Map<TeamId, T>
  directChildMap: Map<TeamId, T[]>
  rootTeams: T[]
  rootTeamIds: Set<TeamId>
}

export const toDirectChildMap = <T extends WithParentId>(teams: T[]): Map<TeamId, T[]> => {
  const map = new Map<TeamId, T[]>()
  for (const team of teams) {
    const parent = team.parentId
    if (!parent) continue
    const children = map.get(parent) ?? []
    children.push(team)
    map.set(parent, children)
  }
  return map
}

export const toTeamRelations = <T extends SortableTeam>(teams: T[]): TeamRelations<T> => {
  const teamsById = new Map<TeamId, T>(teams.map((team) => [team.teamId, team]))
  const rootTeams = teams.filter(
    (team) => isNotDefined(getParentTeam(team, teamsById)) || hasCyclicAncestry(team, teamsById)
  )
  return {
    teamsById,
    directChildMap: toDirectChildMap(teams),
    rootTeams,
    rootTeamIds: new Set(rootTeams.map((team) => team.teamId)),
  }
}

const hasCyclicAncestry = <T extends SortableTeam>(team: T, teamsById: Map<TeamId, T>): boolean => {
  const visited = new Set<TeamId>([team.teamId])
  let parent = getParentTeam(team, teamsById)
  while (isDefined(parent)) {
    if (visited.has(parent.teamId)) return true
    visited.add(parent.teamId)
    parent = getParentTeam(parent, teamsById)
  }
  return false
}

export const getParentTeam = <T extends SortableTeam>(team: T, teamsById: Map<TeamId, T>): T | undefined => {
  if (isNotDefined(team.parentId)) return undefined
  return teamsById.get(team.parentId)
}

export const getAncestorTeams = <T extends SortableTeam>(team: T, relations: TeamRelations<T>): T[] => {
  const ancestors: T[] = []
  const visited = new Set<TeamId>([team.teamId])
  let parent = getParentTeam(team, relations.teamsById)

  while (isDefined(parent) && !visited.has(parent.teamId)) {
    ancestors.unshift(parent)
    visited.add(parent.teamId)
    parent = getParentTeam(parent, relations.teamsById)
  }

  return ancestors
}

export const getPeerTeams = <T extends SortableTeam>(team: T, relations: TeamRelations<T>): T[] => {
  if (relations.rootTeamIds.has(team.teamId)) return relations.rootTeams
  const parent = getParentTeam(team, relations.teamsById)
  if (isNotDefined(parent)) return relations.rootTeams
  return relations.directChildMap.get(parent.teamId) ?? []
}

export const getDirectChildTeams = <T extends SortableTeam>(team: T, relations: TeamRelations<T>): T[] => {
  return relations.directChildMap.get(team.teamId) ?? []
}

export const compareTeamsForRow =
  <T extends SortableTeam>(directChildMap: Map<TeamId, T[]>) =>
  (a: T, b: T): number => {
    const aChildren = directChildMap.get(a.teamId)?.length ?? 0
    const bChildren = directChildMap.get(b.teamId)?.length ?? 0
    if (aChildren !== bChildren) return bChildren - aChildren
    return a.name.localeCompare(b.name, undefined, { numeric: true })
  }

export type CapTeamsForRowInput<T extends SortableTeam> = {
  teams: T[]
  directChildMap: Map<TeamId, T[]>
  pinnedTeamId?: TeamId
  expanded?: boolean
  limit?: number
}

export type CappedRowTeams<T extends SortableTeam> = {
  visible: T[]
  overflowCount: number
}

export const capTeamsForRow = <T extends SortableTeam>({
  teams,
  directChildMap,
  pinnedTeamId,
  expanded = false,
  limit = MAX_TEAMS_PER_LEVEL,
}: CapTeamsForRowInput<T>): CappedRowTeams<T> => {
  const cap = toRowCap(limit)
  const compare = compareTeamsForRow(directChildMap)
  const ranked = [...teams].sort(compare)
  const overflowCount = Math.max(ranked.length - cap, 0)
  if (expanded || overflowCount === 0) return { visible: ranked, overflowCount }

  const visible = ranked.slice(0, cap)
  if (visible.some((team) => team.teamId === pinnedTeamId)) return { visible, overflowCount }

  const anchor = ranked.find((team) => team.teamId === pinnedTeamId)
  if (isNotDefined(anchor)) return { visible, overflowCount }

  return { visible: [...visible.slice(0, cap - 1), anchor].sort(compare), overflowCount }
}

const toRowCap = (limit: number): number =>
  Number.isFinite(limit) ? Math.max(1, Math.trunc(limit)) : MAX_TEAMS_PER_LEVEL
